<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('afterdeparture.Animal') }}</h5>
    <!--p class="mb-0 text-sm">{{ $t('afterdeparture.NB_21_days_rule') }}</p-->
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('afterdeparture.Selected_animals') }} ({{ selectedAnimals.length }})</p>
            <div class="row">
              <div v-for="animal in selectedAnimals" :key="animal.animalId" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(animal)">                  
                  <span v-html="displayText(animal)"></span>
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(animal)"
                  >
                    ×
                  </span>
                </div>
              </div>
              <p class="text-danger text-sm" v-if="selectedAnimals.length === 0" style="margin-bottom: 2px !important;">
                {{ $t('common.No_animal_selected') }}
              </p>
              <p class="text-danger text-sm" v-if="v$.selectedAnimalIds.$error">
                {{ v$.selectedAnimalIds.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('afterdeparture.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('afterdeparture.Search_UBN_studbooknumber_animalnumber_name')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available animals -->
          <p class="fw-bold text-sm">{{ $t('afterdeparture.Available_animals') }}</p>

          <div class="available-animals-list">
              <div class="row">
                  <div
                      v-for="animal in filteredAnimals"
                      :key="animal.animalId"
                      class="col-md-6 mb-1"
                  >
                      <div
                          class="card border text-sm"
                          :class="{'border-success bg-light': isSelected(animal.id), 'cursor-pointer': true}"
                          @click="toggleSelect(animal)"
                      >
                          <span v-html="displayText(animal)"></span>
                      </div>
                  </div>
              </div>
          </div>          
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { required, helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "AfterDeparture_Animals",
    props: {
      animals: Array,
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()      
      const localFormData = reactive(props.formData);
      
      const searchQuery = ref('')

      const selectedAnimalIds = ref(String(props.formData.animalIds || '').split(',').filter(animalId => animalId))

      const isSelected = (id) => selectedAnimalIds.value.includes(id)

      const toggleSelect = (animal) => {
          const animalId = animal.animalId;

          const index = selectedAnimalIds.value.indexOf(animalId);

          if (index > -1) {
              selectedAnimalIds.value.splice(index, 1);
          } else {
              selectedAnimalIds.value.push(animalId);
          }
      }

      const displayText = (f) => {
          let parts = [];

          if (f.animalStudbookNumber) parts.push(f.animalStudbookNumber);
          if (f.animalNumber) parts.push(f.animalNumber);
          if (f.animalName) parts.push(`(${f.animalName})`);

          if (f.arrivalDate) {
              parts.push(`<span style="color: green">${f.arrivalDate}</span>`);
          }

          if (f.breedCode) parts.push(`(${f.breedCode})`);
          if (f.departureReason) parts.push(f.departureReason);

          const icon = `
            <span style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                width:18px;
                height:18px;
                margin-right:4px;
                vertical-align:middle;
            ">
                <i class="material-symbols-rounded"
                  style="
                      font-size:15px !important;
                      line-height:1 !important;
                      color:#ff9800 !important;
                      display:block;
                  ">output</i>
            </span>
        `;
          return icon + parts.join(' | ');
      };
            
      const selectedAnimals = computed(() =>
        props.animals.filter(f => selectedAnimalIds.value.includes(f.animalId))
      )

      const filteredAnimals = computed(() =>
        props.animals.filter(f =>
          !selectedAnimalIds.value.includes(f.animalId) &&
          displayText(f).toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      )

      // Vuelidate rules – require at least one selected animal
      const rules = computed(() => ({
        selectedAnimalIds: {
          required: helpers.withMessage(t('afterdeparture.Select_at_least_one_animal'), required),
          minOne: helpers.withMessage(t('afterdeparture.Select_at_least_one_animal'), value => value.length > 0)
        }
      }))
      
      //const v$ = useVuelidate(rules, localFormData)
      const v$ = useVuelidate(rules, { selectedAnimalIds })

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })

      const validateAndPrevStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.animalIds = selectedAnimalIds.value
          props.prevStep()
        }
      }

      const validateAndNextStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.animalIds = selectedAnimalIds.value // Store in formData if needed
          props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedAnimalIds,
        selectedAnimals,
        filteredAnimals,
        isSelected,
        toggleSelect,
        displayText,
        v$,
        validateAndPrevStep,
        validateAndNextStep
      }
    }    
};
</script>
<style scoped>
  .card {
    transition: background-color 0.2s ease-in-out;
    cursor: pointer;
    padding: 10px !important;
  }
  .available-animals-list {

        max-height: 400px;
        overflow-y: auto;
        overflow-x: hidden;

        padding: 6px 12px 6px 4px;
        border: 1px solid #e9ecef;
        border-radius: 10px;
        background: #fafafa;

        scrollbar-width: thin;
        scrollbar-color: #adb5bd transparent;
    }

    .available-animals-list::-webkit-scrollbar {
        width: 7px;
    }

    .available-animals-list::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 10px;
    }

    .available-animals-list::-webkit-scrollbar-thumb {
        background: #adb5bd;
        border-radius: 10px;
    }

    .available-animals-list::-webkit-scrollbar-thumb:hover {
        background: #6c757d;
    }
</style>